import { notificationAPI } from "../api/notificationAPI";

export type EnablePushResult =
  | { ok: true }
  | { ok: false; reason: "UNSUPPORTED" | "NOT_CONFIGURED" | NotificationPermission };

const SW_URL = "/sw.js";

// Users who switched push off in settings. Kept per user so a granted
// browser permission doesn't silently re-subscribe them on the next login.
// Survives logout — see `preservePushOptOut`.
export const PUSH_OPT_OUT_KEY = "notification_push_opt_out";

const readOptOutMap = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(PUSH_OPT_OUT_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

const setOptOut = (userUuid: string | null | undefined, optedOut: boolean) => {
  if (!userUuid) return;
  try {
    const map = readOptOutMap();
    if (optedOut) map[userUuid] = true;
    else delete map[userUuid];
    localStorage.setItem(PUSH_OPT_OUT_KEY, JSON.stringify(map));
  } catch {
    // Storage blocked — worst case push is re-offered on next login.
  }
};

export const isPushOptedOut = (userUuid: string | null | undefined): boolean =>
  !!userUuid && !!readOptOutMap()[userUuid];

// Logout wipes localStorage; call this around the wipe so the opt-out
// choice isn't lost with the session.
export const preservePushOptOut = (clear: () => void) => {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(PUSH_OPT_OUT_KEY);
  } catch {
    // ignore
  }
  clear();
  if (saved) {
    try {
      localStorage.setItem(PUSH_OPT_OUT_KEY, saved);
    } catch {
      // ignore
    }
  }
};

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw.split("").map((c) => c.charCodeAt(0)));
};

const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array): boolean => {
  if (!a) return false;
  const view = new Uint8Array(a);
  return view.length === b.length && view.every((byte, i) => byte === b[i]);
};

export const isPushSupported = (): boolean =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const getCurrentSubscription = async (): Promise<PushSubscription | null> => {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
};

// Unregister first, then unsubscribe — the other way round loses the
// endpoint before the server hears about it, leaving a dead row behind.
// `force` still drops the local subscription when the server call fails
// (logout): the push service then answers 410 and the backend marks the
// device INACTIVE on its own.
const dropSubscription = async (sub: PushSubscription, force = false): Promise<void> => {
  try {
    // `{ ok: true, skipped: "NOT_FOUND" }` is success too — nothing to handle.
    await notificationAPI.unregisterDevice({ endpoint: sub.endpoint });
  } catch (error) {
    if (!force) throw error;
    console.error("Failed to unregister push device:", error);
  }
  await sub.unsubscribe();
};

const registerSubscription = (sub: PushSubscription) => {
  const json = sub.toJSON();
  return notificationAPI.registerDevice({
    endpoint: json.endpoint as string,
    p256dh_key: json.keys!.p256dh,
    auth_key: json.keys!.auth,
    platform: "web",
    user_agent: navigator.userAgent,
  });
};

// Subscribes (or reuses the existing subscription) and registers it with
// the backend. A subscription is tied to the VAPID key it was made with, so
// if the server key changed, the old one is dropped and a fresh one made —
// otherwise every push to it returns 403 forever.
const subscribeAndRegister = async (): Promise<EnablePushResult> => {
  const { data } = await notificationAPI.getVapidPublicKey();
  if (!data.configured) return { ok: false, reason: "NOT_CONFIGURED" };

  const serverKey = urlBase64ToUint8Array(data.public_key);
  await navigator.serviceWorker.register(SW_URL);
  const reg = await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options.applicationServerKey, serverKey)) {
    await dropSubscription(sub, true);
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: serverKey,
    });
  }

  // Re-registering the same endpoint is an upsert, so this is safe to repeat.
  await registerSubscription(sub);
  return { ok: true };
};

// User-initiated: may show the browser permission prompt.
export const enablePush = async (userUuid?: string | null): Promise<EnablePushResult> => {
  if (!isPushSupported()) return { ok: false, reason: "UNSUPPORTED" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: permission };

  const res = await subscribeAndRegister();
  if (res.ok) setOptOut(userUuid, false);
  return res;
};

// User-initiated "turn off" from settings. Throws if the server can't be
// told, so the toggle can stay on and show an error.
export const disablePush = async (userUuid?: string | null): Promise<void> => {
  const sub = await getCurrentSubscription();
  if (sub) await dropSubscription(sub);
  setOptOut(userUuid, true);
};

// "Re-enable notifications" — needed after the VAPID keys change or when
// push starts returning 403. Always drops the current subscription and
// makes a new one with whatever key the server has now.
export const resubscribePush = async (userUuid?: string | null): Promise<EnablePushResult> => {
  if (!isPushSupported()) return { ok: false, reason: "UNSUPPORTED" };
  if (Notification.permission !== "granted") return enablePush(userUuid);

  const old = await getCurrentSubscription();
  if (old) await dropSubscription(old, true);

  const res = await subscribeAndRegister();
  if (res.ok) setOptOut(userUuid, false);
  return res;
};

// On login: keep this browser registered for the user, without ever
// prompting. Skipped when permission isn't granted or the user switched
// push off in settings.
export const syncPushSubscription = async (userUuid?: string | null): Promise<void> => {
  if (!isPushSupported() || Notification.permission !== "granted") return;
  if (isPushOptedOut(userUuid)) return;
  try {
    await subscribeAndRegister();
  } catch (error) {
    console.error("Failed to sync push subscription:", error);
  }
};

// Logout: must never block signing out, and must always drop the local
// subscription so this browser stops receiving the previous user's pushes.
// Does not record an opt-out — logging out isn't turning push off.
export const disablePushSilently = async (): Promise<void> => {
  try {
    const sub = await getCurrentSubscription();
    if (sub) await dropSubscription(sub, true);
  } catch (error) {
    console.error("Failed to unregister push device:", error);
  }
};

export const getPushSubscriptionEndpoint = async (): Promise<string | null> => {
  const sub = await getCurrentSubscription();
  return sub?.endpoint ?? null;
};

// Whether this browser is currently subscribed and allowed to show pushes.
export const isPushActive = async (): Promise<boolean> => {
  if (!isPushSupported() || Notification.permission !== "granted") return false;
  return !!(await getCurrentSubscription());
};
